import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Container, Paper, Stack, Typography } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useNavigate, useParams } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import server from '../../global/server';
import logo from '../../assets/logo.png';

/**
 * Página em que o responsável autoriza ou recusa o cadastro de um adolescente
 * de 13 a 17 anos (Termos 2.2, art. 14 §1º da LGPD).
 *
 * Pública e sem login: quem responde não tem conta na Empiria. O que identifica
 * o pedido é o token do link enviado por e-mail.
 */
const GuardianConsent = () => {
    const { token } = useParams();
    const navigate = useNavigate();

    const [pedido, setPedido] = useState(null);
    const [erro, setErro] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [enviando, setEnviando] = useState(false);
    const [desfecho, setDesfecho] = useState(null);

    const carregar = useCallback(async () => {
        try {
            const { data } = await server.get(`/auth/autorizacao/${token}`);
            setPedido(data.data);
        } catch (error) {
            setErro(error.response?.data?.message || 'Não foi possível abrir este pedido.');
        } finally {
            setCarregando(false);
        }
    }, [token]);

    useEffect(() => {
        carregar();
    }, [carregar]);

    const responder = async autorizado => {
        try {
            setEnviando(true);
            const { data } = await server.post(`/auth/autorizacao/${token}`, { autorizado });
            setDesfecho(data.message);
        } catch (error) {
            setErro(error.response?.data?.message || 'Não foi possível registrar a sua resposta.');
        } finally {
            setEnviando(false);
        }
    };

    const Moldura = ({ children }) => (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 6 }}>
            <Container maxWidth="sm">
                <Stack alignItems="center" sx={{ mb: 3 }}>
                    <Box component="img" src={logo} alt="Empiria" sx={{ height: 48, objectFit: 'contain' }} />
                </Stack>
                <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 }, borderRadius: '16px' }}>{children}</Paper>
            </Container>
        </Box>
    );

    if (carregando) {
        return (
            <Moldura>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                    Abrindo o pedido…
                </Typography>
            </Moldura>
        );
    }

    if (desfecho) {
        return (
            <Moldura>
                <Stack alignItems="center" spacing={2}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 64, color: '#2e7d32' }} />
                    <Typography variant="h6" fontWeight={700} textAlign="center">
                        Resposta registrada
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                        {desfecho}
                    </Typography>
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={() => navigate('/')}
                        sx={{ bgcolor: '#006a61', textTransform: 'none', height: 44, '&:hover': { bgcolor: '#005a52' } }}
                    >
                        Ir para a Empiria
                    </Button>
                </Stack>
            </Moldura>
        );
    }

    if (erro) {
        return (
            <Moldura>
                <Alert severity="error" sx={{ borderRadius: '12px' }}>
                    {erro}
                </Alert>
            </Moldura>
        );
    }

    if (pedido?.ja_respondido) {
        return (
            <Moldura>
                <Alert severity="info" sx={{ borderRadius: '12px' }}>
                    Este cadastro já foi autorizado.
                </Alert>
            </Moldura>
        );
    }

    if (pedido?.expirado) {
        return (
            <Moldura>
                <Alert severity="warning" sx={{ borderRadius: '12px' }}>
                    Este link expirou. Peça para {pedido.user_name} fazer o cadastro de novo.
                </Alert>
            </Moldura>
        );
    }

    return (
        <Moldura>
            <Stack spacing={2.5}>
                <Typography variant="h6" fontWeight={700}>
                    Autorização de responsável
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    <strong>{pedido?.user_name}</strong>, de {pedido?.idade} anos, criou uma conta na Empiria e
                    indicou você como responsável. A Empiria é uma plataforma de encontros online por
                    videoconferência, em que a pessoa pode participar de encontros criados por anfitriões.
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    Por ter menos de 18 anos, o cadastro depende da sua autorização específica, nos termos do
                    artigo 14, §1º da Lei Geral de Proteção de Dados. Enquanto você não responder, a conta
                    existe mas não pode ser usada.
                </Typography>

                <Alert severity="info" sx={{ borderRadius: '12px' }}>
                    Se recusar, a conta e os dados são excluídos. Você também pode pedir o cancelamento mais
                    tarde, a qualquer momento.
                </Alert>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <LoadingButton
                        fullWidth
                        variant="contained"
                        loading={enviando}
                        onClick={() => responder(true)}
                        sx={{ bgcolor: '#006a61', textTransform: 'none', height: 44, '&:hover': { bgcolor: '#005a52' } }}
                    >
                        Autorizar o cadastro
                    </LoadingButton>
                    <LoadingButton
                        fullWidth
                        variant="outlined"
                        color="error"
                        loading={enviando}
                        onClick={() => responder(false)}
                        sx={{ textTransform: 'none', height: 44 }}
                    >
                        Recusar e excluir
                    </LoadingButton>
                </Stack>
            </Stack>
        </Moldura>
    );
};

export default GuardianConsent;
